import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  formatMoney,
  type ReconciliationCurrency,
  type SavedStatementResponse,
  type StatementReport,
} from '@greencandle/api-client'
import { api } from '../api'
import { useAccounts } from '../terminal/queries'

/**
 * FR-B1..B4 and FR-H3, inside the terminal.
 *
 * <p>The same three acts as the standalone import page, in the terminal's own palette: read the
 * PDF, show what it says, and only then write it into an account. They stay three acts rather
 * than one button because under D-04 reading a statement and believing it are different things
 * - a statement whose parsed cash does not equal its own Cash Report totals cannot be committed
 * at all, and this screen shows that arithmetic rather than asserting it went well.
 *
 * <p>Nothing here writes to the portfolio until the Import button is pressed, and the preview
 * before it says exactly how many rows are new. IBKR statements overlap heavily, so most of a
 * later statement is usually already present; that is reported, not silently deduplicated.
 */

export interface ReportsHandle {
  /** Opens the file picker, so the button in the sub-nav can reach it. */
  choose(): void
}

export const TerminalReports = forwardRef<ReportsHandle>(function TerminalReports(_props, ref) {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  useImperativeHandle(ref, () => ({ choose: () => fileInput.current?.click() }), [])

  const saved = useQuery({ queryKey: ['statements'], queryFn: () => api.savedStatements() })

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadStatement(file),
    onSuccess: (result) => {
      setOpenId(result.summary.id)
      void queryClient.invalidateQueries({ queryKey: ['statements'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteSavedStatement(id),
    onSuccess: (_data, id) => {
      if (openId === id) setOpenId(null)
      void queryClient.invalidateQueries({ queryKey: ['statements'] })
    },
  })

  const opened = useQuery({
    queryKey: ['statement', openId],
    queryFn: () => api.savedStatement(openId!),
    enabled: openId !== null && upload.data?.summary.id !== openId,
  })

  const shown: SavedStatementResponse | undefined =
    upload.data?.summary.id === openId ? upload.data : opened.data

  function accept(files: FileList | null) {
    const file = files?.[0]
    if (file) upload.mutate(file)
  }

  return (
    <>
      <section className="panel">
        <div className="panel-header">
          <h3>Load an IBKR statement</h3>
          <span className="panel-note">Activity Statement · PDF or CSV</span>
        </div>

        <div
          className={`term-drop${dragging ? ' dragging' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            accept(e.dataTransfer.files)
          }}
        >
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,.pdf,text/csv,.csv"
            className="visually-hidden"
            onChange={(e) => {
              accept(e.target.files)
              // Cleared so choosing the same file fires change again - re-reading the very
              // same statement is the natural thing to do after a parser fix.
              e.target.value = ''
            }}
          />
          <button
            type="button"
            className="btn-primary"
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? 'Reading…' : 'Choose a PDF or CSV'}
          </button>
          <p>or drop one here</p>
        </div>

        {upload.isError && (
          <p className="panel-foot warn" role="alert">
            {upload.error instanceof ApiError
              ? (upload.error.problem.detail ?? upload.error.message)
              : 'The upload failed.'}
          </p>
        )}

        {upload.data?.alreadyExisted && (
          <p className="panel-foot">
            This file has been read before. Showing the stored report rather than a second copy.
          </p>
        )}
      </section>

      {shown && <Report report={shown.report} reportId={shown.summary.id} />}

      <section className="panel">
        <div className="panel-header">
          <h3>Statements read</h3>
          <span className="panel-note">{saved.data?.length ?? 0} total</span>
        </div>

        {saved.isPending ? (
          <p className="panel-foot">Loading…</p>
        ) : saved.data && saved.data.length > 0 ? (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Period</th>
                  <th className="right">Trades</th>
                  <th className="right">Cash</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {saved.data.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="term-link"
                        onClick={() => setOpenId(openId === row.id ? null : row.id)}
                        aria-expanded={openId === row.id}
                      >
                        {openId === row.id ? '▾' : '▸'} {row.filename}
                      </button>
                    </td>
                    <td className="num">
                      {row.periodStart ?? '—'} — {row.periodEnd ?? '—'}
                    </td>
                    <td className="right num">{row.tradeCount}</td>
                    <td className="right num">{row.cashFlowCount}</td>
                    <td>
                      <span className={`badge ${row.importable ? 'badge-re' : 'badge-custom'}`}>
                        {row.importable ? 'reconciles' : 'does not reconcile'}
                      </span>
                      {row.unparsedCount > 0 && (
                        <span className="cell-sub"> · {row.unparsedCount} unparsed</span>
                      )}
                    </td>
                    <td className="right">
                      <button
                        type="button"
                        className="term-link danger"
                        onClick={() => remove.mutate(row.id)}
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="panel-foot">Nothing read yet.</p>
        )}
      </section>
    </>
  )
})

function Report({ report, reportId }: { report: StatementReport; reportId: string }) {
  const ok = report.verdict.importable

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>Statement report</h3>
        <span className="panel-note">
          {report.header.periodStart ?? '—'} — {report.header.periodEnd ?? '—'} ·{' '}
          {report.header.baseCurrency ?? '—'}
        </span>
      </div>

      {/* The verdict leads, because it is the only thing that decides whether anything below
          it may be written. D-04 makes this a block rather than a warning. */}
      <div className={`term-verdict ${ok ? 'ok' : 'bad'}`}>
        <span className={`badge ${ok ? 'badge-re' : 'badge-custom'}`}>
          {ok ? 'reconciles' : 'does not reconcile'}
        </span>
        <strong>{report.verdict.headline}</strong>
        {report.verdict.reasons.length > 0 && (
          <ul>
            {report.verdict.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="risk-figures">
        <Figure label="Trades" value={report.totals.tradeCount} />
        <Figure label="Cash movements" value={report.totals.cashFlowCount} />
        <Figure label="Instruments" value={report.totals.instrumentCount} />
        <Figure
          label="Unparsed"
          value={report.totals.unparsedCount}
          tone={report.totals.unparsedCount > 0 ? 'down' : undefined}
        />
      </div>

      {report.reconciliation.currencies.map((currency) => (
        <Reconciliation key={currency.currency} currency={currency} />
      ))}

      {report.unparsed.length > 0 && (
        <>
          <div className="panel-header">
            <h3>Rows that could not be read</h3>
            <span className="panel-note">FR-B4 · surfaced in full rather than dropped</span>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Section</th>
                  <th className="right">Page</th>
                  <th>Reason</th>
                  <th>Raw line</th>
                </tr>
              </thead>
              <tbody>
                {report.unparsed.slice(0, 20).map((row, i) => (
                  <tr key={`${row.section}-${i}`}>
                    <td>{row.section}</td>
                    <td className="right num">{row.page}</td>
                    <td className="cell-sub">{row.reason}</td>
                    <td className="num" style={{ fontSize: 11 }}>
                      {row.rawLine}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {ok ? (
        <CommitPanel reportId={reportId} />
      ) : (
        <p className="panel-foot warn">
          Import is blocked. Until the parsed cash movements equal the statement's own Cash
          Report, not one row reaches the portfolio — that is D-04, not a warning.
        </p>
      )}
    </section>
  )
}

function Reconciliation({ currency }: { currency: ReconciliationCurrency }) {
  return (
    <>
      <div className="panel-header">
        <h3>
          Reconciliation · {currency.currency}{' '}
          <span className={`badge ${currency.balanced ? 'badge-re' : 'badge-custom'}`}>
            {currency.balanced ? 'reconciles' : 'mismatch'}
          </span>
        </h3>
        <span className="panel-note">
          {currency.internallyConsistent
            ? 'Cash Report read'
            : 'Cash Report does not close on itself'}
        </span>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Line</th>
              <th className="right">Statement</th>
              <th className="right">Parsed</th>
              <th className="right">Difference</th>
            </tr>
          </thead>
          <tbody>
            {currency.lines.map((line) => (
              <tr key={line.label}>
                <td>{line.label}</td>
                <td className="right num">{formatMoney(line.statement, currency.currency)}</td>
                <td className="right num">{formatMoney(line.parsed, currency.currency)}</td>
                <td className={`right num ${line.balanced ? '' : 'down'}`}>
                  {formatMoney(line.difference, currency.currency)}
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <strong>Ending cash</strong>
              </td>
              <td className="right num">
                {formatMoney(currency.endingCash, currency.currency)}
              </td>
              <td className="right num">
                {formatMoney(currency.impliedEndingCash, currency.currency)}
              </td>
              <td className="right" />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  )
}

function CommitPanel({ reportId }: { reportId: string }) {
  const queryClient = useQueryClient()
  const [accountId, setAccountId] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const accounts = useAccounts()
  const target = accountId || accounts.data?.[0]?.id || ''

  const preview = useQuery({
    queryKey: ['commit-preview', reportId, target],
    queryFn: () => api.previewCommit(reportId, target),
    enabled: target !== '',
  })

  const commit = useMutation({
    mutationFn: () => api.commitStatement(reportId, target),
    onSuccess: () => {
      setConfirmed(true)
      // Holdings, allocation, snapshots, risk - everything downstream moves at once.
      void queryClient.invalidateQueries()
    },
  })

  if (accounts.isPending) return null
  if (!accounts.data || accounts.data.length === 0) {
    return <p className="panel-foot warn">Create an account to import into first.</p>
  }

  const p = preview.data
  const nothingNew = p != null && p.tradesCreated + p.cashFlowsCreated === 0

  return (
    <div className="controls-box">
      <div className="control-group">
        <label htmlFor="commit-account">Import into</label>
        <select
          id="commit-account"
          value={target}
          onChange={(e) => {
            setAccountId(e.target.value)
            setConfirmed(false)
          }}
        >
          {accounts.data.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.baseCurrency})
            </option>
          ))}
        </select>
      </div>

      <div className="control-group control-end">
        <button
          type="button"
          className="btn-primary"
          disabled={!p || commit.isPending || nothingNew}
          onClick={() => commit.mutate()}
        >
          {commit.isPending ? 'Importing…' : 'Import'}
        </button>
      </div>

      <div className="commit-note">
        {preview.isPending && <span>Working out what is new…</span>}
        {p && !confirmed && (
          <span>
            Would write <strong>{p.tradesCreated}</strong> trades and{' '}
            <strong>{p.cashFlowsCreated}</strong> cash movements.
            {p.tradesAlreadyPresent + p.cashFlowsAlreadyPresent > 0 && (
              <>
                {' '}
                {p.tradesAlreadyPresent + p.cashFlowsAlreadyPresent} rows are already present from an
                overlapping statement and will be skipped.
              </>
            )}
            {nothingNew && ' Nothing here is new.'}
          </span>
        )}
        {commit.isError && (
          <span className="down" role="alert">
            {commit.error instanceof ApiError
              ? (commit.error.problem.detail ?? commit.error.message)
              : 'The import failed.'}
          </span>
        )}
        {confirmed && commit.data && (
          <span className="up">
            Imported {commit.data.tradesCreated} trades and{' '}
            {commit.data.cashFlowsCreated} cash movements; skipped{' '}
            {commit.data.tradesAlreadyPresent + commit.data.cashFlowsAlreadyPresent}.
          </span>
        )}
      </div>
    </div>
  )
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'down'
}) {
  return (
    <div className="risk-figure">
      <span className="label">{label}</span>
      <span className={`value ${tone ?? ''}`}>{value}</span>
    </div>
  )
}
